import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Plus, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface EntityOption {
  value: string;
  label: string;
}

interface EntitySelectProps {
  /** Selected id as a string; '' = nothing selected. */
  value: string;
  onChange: (value: string) => void;
  /** Server-side search (debounced). Empty query should return a first page. */
  search: (query: string) => Promise<EntityOption[]>;
  /** Resolve a label for a preselected value we don't have a label for (edit mode). */
  resolveLabel?: (value: string) => Promise<string | null>;
  /** Base react-query key for this picker (e.g. 'company-picker'). */
  queryKey: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  /** Show an inline clear (✕) to reset the selection — handy for filters. */
  clearable?: boolean;
  /** Override the trigger's classes (e.g. to size it tight on a filter bar). */
  triggerClassName?: string;
  /** When set, offer an inline "Create …" item for the typed text (no exact
   *  match). Should create the record server-side and return its option. */
  onCreate?: (query: string) => Promise<EntityOption>;
  /** Label for the create item; defaults to `Create "<query>"`. */
  createLabel?: (query: string) => string;
}

function useDebounced<T>(value: T, ms = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export function EntitySelect({
  value,
  onChange,
  search,
  resolveLabel,
  queryKey,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'No results.',
  disabled,
  clearable = false,
  triggerClassName,
  onCreate,
  createLabel,
}: EntitySelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query);
  const [pickedLabel, setPickedLabel] = useState('');
  const [creating, setCreating] = useState(false);

  const results = useQuery({
    queryKey: [queryKey, 'search', debounced],
    queryFn: () => search(debounced),
    enabled: open,
    placeholderData: (prev) => prev,
  });

  // Label a preselected value (e.g. edit mode) when we weren't given one.
  const resolved = useQuery({
    queryKey: [queryKey, 'resolve', value],
    queryFn: () => resolveLabel!(value),
    enabled: Boolean(value) && !pickedLabel && Boolean(resolveLabel),
  });

  // Gate on `value` so a stale remembered label never lingers after an external
  // clear (e.g. "reset filters") — no selection ⇒ show the placeholder.
  const displayLabel = value ? pickedLabel || resolved.data || '…' : '';
  const options = results.data ?? [];

  const trimmed = query.trim();
  const exactMatch = options.some((o) => o.label.toLowerCase() === trimmed.toLowerCase());
  const canCreate = Boolean(onCreate) && trimmed.length > 0 && !exactMatch;

  async function handleCreate() {
    if (!onCreate) return;
    setCreating(true);
    try {
      const opt = await onCreate(trimmed);
      onChange(opt.value);
      setPickedLabel(opt.label);
      setQuery('');
      setOpen(false);
    } catch {
      // Keep the popover open on failure; the API layer surfaces the error.
    } finally {
      setCreating(false);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            // `min-w-0` lets the trigger shrink inside grid/flex parents
            // (FormItem is a grid) so the label span truncates instead of the
            // long text propagating its width up and overflowing the dialog.
            'w-full min-w-0 justify-between font-normal',
            !displayLabel && 'text-muted-foreground',
            triggerClassName,
          )}
        >
          <span className="truncate">{displayLabel || placeholder}</span>
          {clearable && value ? (
            <span
              role="button"
              tabIndex={-1}
              aria-label="Clear selection"
              className="hover:bg-accent ml-1 shrink-0 rounded p-0.5"
              onPointerDown={(e) => {
                // Clear without opening the popover.
                e.preventDefault();
                e.stopPropagation();
                onChange('');
                setPickedLabel('');
              }}
            >
              <X className="size-4 opacity-60" />
            </span>
          ) : (
            <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1" align="start">
        {/* overflow-visible so the search input's focus ring isn't clipped at the
            top by cmdk's default overflow-hidden; popover p-1 gives the ring room. */}
        <Command shouldFilter={false} className="overflow-visible">
          <CommandInput
            placeholder={searchPlaceholder}
            value={query}
            onValueChange={setQuery}
            className="rounded-md"
          />
          <CommandList>
            {results.isFetching ? (
              <div className="text-muted-foreground p-3 text-sm">Searching…</div>
            ) : (
              <>
                {options.length === 0 && !canCreate ? (
                  <div className="text-muted-foreground p-3 text-sm">{emptyText}</div>
                ) : null}
                {options.length > 0 ? (
                  <CommandGroup>
                    {options.map((opt) => (
                      <CommandItem
                        key={opt.value}
                        value={opt.value}
                        onSelect={() => {
                          onChange(opt.value);
                          setPickedLabel(opt.label);
                          setOpen(false);
                        }}
                      >
                        <Check
                          className={cn('size-4', opt.value === value ? 'opacity-100' : 'opacity-0')}
                        />
                        {opt.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ) : null}
                {canCreate ? (
                  <CommandGroup heading={options.length > 0 ? 'Create' : undefined}>
                    <CommandItem value="__create__" disabled={creating} onSelect={handleCreate}>
                      <Plus className="size-4" />
                      {createLabel ? createLabel(trimmed) : `Create “${trimmed}”`}
                    </CommandItem>
                  </CommandGroup>
                ) : null}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
